import { ArrowUpRight, Facebook, Globe, Instagram, Play, Quote, Stethoscope, Twitter, Youtube } from "lucide-react";
import { ResilientImage } from "@/components/media/ResilientImage";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Countdown } from "@/components/Countdown";
import { PedigreeTree } from "@/components/PedigreeTree";
import { mediaUrlFromMap } from "@/lib/api";
import { localized } from "@/lib/intl";
import { eventBase } from "@/lib/microsite";

/*
 * Builder-phase block library additions. Same contract as blocks/index.jsx:
 * ({ data, props }) — data is the microsite payload, props the block instance props.
 */

const Section = ({ children, testid, tight = false }) => (
  <section data-testid={testid} className={`max-w-6xl mx-auto px-6 lg:px-12 ${tight ? "py-12" : "py-16 lg:py-24"}`}>
    {children}
  </section>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-10">{children}</h2>
);

const findHorse = (data, horseId) => {
  const item = horseId ? data.lots.find(({ horse }) => horse?.id === horseId) : data.lots[0];
  return item?.horse || data.lots[0]?.horse || null;
};

/** YouTube / Vimeo URL -> embed URL (null if not recognized). */
const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

/* ---------------- video ---------------- */

const VideoBlock = ({ data, props }) => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const src = props?.media_id ? mediaUrlFromMap(data.media, props.media_id) : null;
  const embed = !src ? embedUrl(props?.url) : null;
  if (!src && !embed) return null;
  const title = localized(props?.title, locale);
  return (
    <Section testid="block-video">
      {title && <SectionTitle>{title}</SectionTitle>}
      <figure className="flex flex-col gap-4">
        <div className="relative rounded-3xl overflow-hidden bg-[#0A0A0A] aspect-video">
          {src ? (
            <video src={src} controls playsInline preload="metadata" className="w-full h-full object-contain" />
          ) : (
            <iframe
              src={embed}
              title={title || "video"}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          )}
        </div>
        {localized(props?.caption, locale) && (
          <figcaption className="text-sm text-[#737373]">{localized(props.caption, locale)}</figcaption>
        )}
      </figure>
    </Section>
  );
};

/* ---------------- standalone countdown ---------------- */

const CountdownBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const auction = data.auctions[0];
  if (!auction) return null;
  const started = new Date(auction.starts_at) < new Date();
  const target = started ? auction.ends_at : auction.starts_at;
  if (new Date(target) < new Date()) return null;
  return (
    <section className="bg-[#0A0A0A] eq-grain" data-testid="block-countdown">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14 lg:py-20 flex flex-col items-center gap-6 text-center">
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
          {localized(props?.title, locale) || t(started ? "pub.endsIn" : "pub.startsIn")}
        </span>
        <div className="[&_*]:!text-white [&_.text-\\[\\#0A0A0A\\]\\/20]:!text-white/30 scale-110 sm:scale-125 py-2">
          <Countdown target={target} />
        </div>
      </div>
    </section>
  );
};

/* ---------------- story / history ---------------- */

const StoryBlock = ({ data, props }) => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const title = localized(props?.title, locale);
  const body = localized(props?.body, locale);
  if (!title && !body) return null;
  const imageUrl = mediaUrlFromMap(data.media, props?.image_media_id);
  const imageLeft = props?.layout === "image_left";
  return (
    <Section testid="block-story">
      <div className={`grid grid-cols-1 ${imageUrl ? "lg:grid-cols-2" : ""} gap-10 lg:gap-16 items-center`}>
        <div className={`flex flex-col gap-5 ${imageUrl && imageLeft ? "lg:order-2" : ""}`}>
          {title && <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] leading-tight">{title}</h2>}
          {body && <p className="text-base text-[#0A0A0A]/70 leading-relaxed whitespace-pre-line max-w-xl">{body}</p>}
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={title || ""}
            loading="lazy"
            className={`w-full h-72 lg:h-[26rem] object-cover rounded-3xl border border-black/5 ${imageLeft ? "lg:order-1" : ""}`}
          />
        )}
      </div>
    </Section>
  );
};

/* ---------------- team ---------------- */

const TeamBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const items = (props?.items || []).filter((m) => m.name);
  if (items.length === 0) return null;
  return (
    <Section testid="block-team">
      <SectionTitle>{localized(props?.title, locale) || t("blocks.team")}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((m, i) => {
          const photo = mediaUrlFromMap(data.media, m.media_id);
          return (
            <figure key={i} className="flex flex-col gap-3">
              {photo ? (
                <img src={photo} alt={m.name} loading="lazy" className="w-full aspect-square object-cover rounded-2xl border border-black/5" />
              ) : (
                <div className="w-full aspect-square rounded-2xl bg-[#F9F9F9] border border-black/5 flex items-center justify-center">
                  <span className="text-3xl font-light text-[#0A0A0A]/20">{m.name.charAt(0)}</span>
                </div>
              )}
              <figcaption className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-[#0A0A0A]">{m.name}</span>
                {localized(m.role, locale) && <span className="text-xs text-[#737373]">{localized(m.role, locale)}</span>}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </Section>
  );
};

/* ---------------- testimonials ---------------- */

const TestimonialsBlock = ({ props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const items = (props?.items || []).filter((x) => localized(x.quote, locale));
  if (items.length === 0) return null;
  return (
    <section className="bg-[#F9F9F9] border-y border-black/5" data-testid="block-testimonials">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <SectionTitle>{localized(props?.title, locale) || t("blocks.testimonials")}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((x, i) => (
            <blockquote key={i} className="bg-white rounded-3xl border border-black/5 p-8 flex flex-col gap-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
              <Quote className="w-6 h-6" style={{ color: "var(--brand-accent, #1D4D3B)" }} aria-hidden="true" />
              <p className="text-base text-[#0A0A0A]/80 leading-relaxed flex-1">{localized(x.quote, locale)}</p>
              <footer className="flex flex-col gap-0.5 border-t border-black/5 pt-4">
                <cite className="not-italic text-sm font-medium text-[#0A0A0A]">{x.author}</cite>
                {x.context && <span className="text-xs text-[#737373]">{x.context}</span>}
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- custom CTA ---------------- */

const CtaBlock = ({ props }) => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const title = localized(props?.title, locale);
  const label = localized(props?.button_label, locale);
  if (!title && !label) return null;
  const style = props?.style || "dark";
  const outer =
    style === "dark"
      ? "bg-[#0A0A0A] text-white"
      : style === "accent"
        ? "text-white"
        : "bg-[#F9F9F9] text-[#0A0A0A] border border-black/5";
  const btn =
    style === "light"
      ? "bg-[#0A0A0A] text-white hover:bg-black/80"
      : "bg-white text-[#0A0A0A] hover:bg-white/90";
  return (
    <Section testid="block-cta" tight>
      <div
        className={`rounded-3xl px-8 lg:px-14 py-12 lg:py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 ${outer}`}
        style={style === "accent" ? { backgroundColor: "var(--brand-highlight, var(--brand-accent, #1D4D3B))" } : undefined}
      >
        <div className="flex flex-col gap-2 max-w-xl">
          {title && <h2 className="text-2xl sm:text-3xl font-light tracking-tight leading-tight">{title}</h2>}
          {localized(props?.subtitle, locale) && (
            <p className={`text-sm leading-relaxed ${style === "light" ? "text-[#737373]" : "text-white/70"}`}>
              {localized(props.subtitle, locale)}
            </p>
          )}
        </div>
        {label && (
          <a
            href={props?.button_url || "#lots"}
            target={props?.button_url?.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            data-testid="block-cta-button"
            className={`inline-flex items-center gap-2 rounded-full h-12 px-8 text-sm font-medium transition-colors duration-300 shrink-0 ${btn}`}
          >
            {label}
            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </a>
        )}
      </div>
    </Section>
  );
};

/* ---------------- social links ---------------- */

const SOCIALS = [
  { key: "instagram", Icon: Instagram },
  { key: "facebook", Icon: Facebook },
  { key: "youtube", Icon: Youtube },
  { key: "x", Icon: Twitter },
  { key: "tiktok", Icon: Play },
  { key: "website", Icon: Globe },
];

const SocialLinksBlock = ({ props }) => {
  const { t } = useTranslation();
  const links = SOCIALS.filter(({ key }) => props?.[key]);
  if (links.length === 0) return null;
  return (
    <Section testid="block-social-links" tight>
      <div className="flex flex-col items-center gap-6">
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0A0A0A]/40">{t("blocks.social_links")}</span>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {links.map(({ key, Icon }) => (
            <a
              key={key}
              href={props[key]}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={key}
              data-testid={`social-${key}`}
              className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center text-[#0A0A0A]/60 hover:text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A] transition-colors duration-300"
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ---------------- pedigree highlight ---------------- */

const PedigreeHighlightBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const horse = findHorse(data, props?.horse_id);
  if (!horse?.pedigree) return null;
  return (
    <Section testid="block-pedigree-highlight">
      <SectionTitle>{localized(props?.title, locale) || `${t("pub.pedigree")} — ${horse.name}`}</SectionTitle>
      <PedigreeTree pedigree={horse.pedigree} />
    </Section>
  );
};

/* ---------------- results highlight ---------------- */

const ResultsHighlightBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const horse = findHorse(data, props?.horse_id);
  const results = horse?.results || [];
  if (results.length === 0) return null;
  return (
    <section className="bg-[#0A0A0A] eq-grain" data-testid="block-results-highlight">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 flex flex-col gap-10">
        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">
          {localized(props?.title, locale) || `${t("pub.results")} — ${horse.name}`}
        </h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6">
          {results.slice(0, 9).map((r, i) => (
            <li key={i} className="flex items-baseline gap-4 border-b border-white/10 pb-4">
              <span className="text-sm tabular-nums" style={{ color: "var(--brand-highlight, var(--brand-accent, #1D4D3B))" }}>
                {r.year || "—"}
              </span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-medium text-white truncate">{r.competition}</span>
                <span className="text-xs text-white/50">{r.result}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ---------------- how it works (C1 section) ---------------- */

const HowItWorksBlock = ({ props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const custom = (props?.items || []).filter((it) => localized(it.title, locale) || localized(it.text, locale));
  const steps = custom.length
    ? custom.map((it) => ({ title: localized(it.title, locale), text: localized(it.text, locale) }))
    : [1, 2, 3].map((n) => ({ title: t(`blocks.hiwStep${n}Title`), text: t(`blocks.hiwStep${n}Text`) }));
  const title = localized(props?.title, locale) || t("blocks.hiwDefaultTitle");
  return (
    <Section testid="block-how-it-works">
      <SectionTitle>{title}</SectionTitle>
      <ol className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {steps.map((s, i) => (
          <li key={i} className="rounded-3xl border border-black/5 bg-white p-8 flex flex-col gap-3">
            <span className="w-9 h-9 rounded-full bg-[var(--brand-primary,#1D4D3B)]/10 text-[var(--brand-primary,#1D4D3B)] flex items-center justify-center text-sm font-semibold">
              {i + 1}
            </span>
            <h3 className="text-base font-semibold text-[#0A0A0A]">{s.title}</h3>
            <p className="text-sm text-[#737373] leading-relaxed">{s.text}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
};

/* ---------------- legal terms (C1 section) ---------------- */

const LegalTermsBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const body = localized(props?.body, locale);
  const title = localized(props?.title, locale) || t("blocks.legalTermsDefaultTitle");
  return (
    <Section testid="block-legal-terms" tight>
      <SectionTitle>{title}</SectionTitle>
      {body && <div className="max-w-3xl text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-line">{body}</div>}
      {/* Legal Engine: the binding document is the resolved (pinned) Auction Terms */}
      <Link
        to={`/auction-terms/${data.event.id}`}
        data-testid="block-legal-terms-full-link"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4D3B] hover:underline underline-offset-4"
      >
        {t("blocks.legalTermsReadFull")}
        <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
      </Link>
    </Section>
  );
};

/* ---------------- register a horse (C1 section, flow arrives with consignment) ---------------- */

const RegisterHorseBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const submissions = data.event.submissions;
  if (submissions && submissions.enabled === false) return null; // section off when registrations are closed
  const title = localized(props?.title, locale) || t("blocks.registerHorseDefaultTitle");
  const body = localized(props?.body, locale) || t("blocks.registerHorseDefaultBody");
  return (
    <Section testid="block-register-horse">
      <div className="rounded-3xl bg-[#0A0A0A] px-8 py-14 sm:px-14 flex flex-col sm:flex-row sm:items-center gap-8">
        <div className="flex-1 flex flex-col gap-3">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">{title}</h2>
          <p className="text-sm text-white/60 leading-relaxed max-w-xl">{body}</p>
        </div>
        <Link
          to={`${eventBase(data.organizer?.slug, data.event.slug)}/submit`}
          data-testid="register-horse-cta"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0A0A0A] text-sm font-medium px-7 h-12 hover:bg-white/90 transition-colors duration-300 shrink-0 w-fit"
        >
          {t("blocks.registerHorseCta")}
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </Section>
  );
};

/* ---------------- Phase 4.5 optional modules ---------------- */

/* Vet Info — reassures buyers; auto-counts real vet documents on the lots. */
const VetInfoBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const title = localized(props?.title, locale) || t("blocks.vetDefaultTitle");
  const body = localized(props?.body, locale) || t("blocks.vetDefaultBody");
  const docsCount = (data.lots || []).filter(({ horse }) => (horse?.vet_report_media_ids || []).length > 0).length;
  const contactBits = [props?.vet_name, props?.clinic].filter(Boolean).join(" — ");
  return (
    <Section testid="block-vet-info">
      <div className="rounded-3xl border border-black/5 bg-[#F9F9F8] p-8 lg:p-12 flex flex-col lg:flex-row gap-8 lg:gap-14">
        <div className="w-12 h-12 rounded-2xl bg-[#1D4D3B]/10 flex items-center justify-center shrink-0" aria-hidden="true">
          <Stethoscope className="w-6 h-6 text-[#1D4D3B]" />
        </div>
        <div className="flex flex-col gap-4 max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{title}</h2>
          <p className="text-base text-[#0A0A0A]/70 leading-relaxed whitespace-pre-line">{body}</p>
          {docsCount > 0 && (
            <p className="text-sm font-medium text-[#1D4D3B]" data-testid="vet-info-docs-count">
              {t("blocks.vetDocsCount", { count: docsCount })}
            </p>
          )}
          {(contactBits || props?.email || props?.phone) && (
            <div className="text-sm text-[#737373] flex flex-col gap-0.5 pt-2 border-t border-black/5" data-testid="vet-info-contact">
              {contactBits && <span className="font-medium text-[#0A0A0A]">{contactBits}</span>}
              {props?.email && <a className="hover:underline" href={`mailto:${props.email}`}>{props.email}</a>}
              {props?.phone && <span>{props.phone}</span>}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

/* Services — transport, stabling, financing… ships with sensible defaults. */
const ServicesBlock = ({ props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const custom = (props?.items || []).filter((s) => localized(s.title, locale));
  const items = custom.length
    ? custom.map((s) => ({ title: localized(s.title, locale), text: localized(s.text, locale) }))
    : [1, 2, 3].map((n) => ({ title: t(`blocks.serviceDefault${n}Title`), text: t(`blocks.serviceDefault${n}Text`) }));
  return (
    <Section testid="block-services">
      <SectionTitle>{localized(props?.title, locale) || t("blocks.servicesDefaultTitle")}</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s, i) => (
          <div key={i} className="rounded-2xl border border-black/5 p-7 flex flex-col gap-2.5" data-testid={`service-card-${i}`}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D4D3B]">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="text-lg font-semibold text-[#0A0A0A] tracking-tight">{s.title}</h3>
            {s.text && <p className="text-sm text-[#737373] leading-relaxed">{s.text}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
};

/* Previous editions — the organizer's real track record, computed server-side. */
const PreviousEditionsBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const editions = data.past_events || [];
  if (editions.length === 0) return null;
  return (
    <Section testid="block-previous-editions">
      <SectionTitle>{localized(props?.title, locale) || t("blocks.editionsDefaultTitle")}</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {editions.map((e) => {
          const hero = mediaUrlFromMap(data.media, e.hero_media_id);
          const year = e.starts_at ? new Date(e.starts_at).getFullYear() : "";
          return (
            <Link
              key={e.slug}
              to={eventBase(data.organizer?.slug, e.slug)}
              data-testid={`edition-card-${e.slug}`}
              className="group rounded-2xl border border-black/5 overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
            >
              {hero ? (
                <ResilientImage src={hero} alt="" className="h-40 w-full" testId={`edition-hero-${e.slug}`} />
              ) : (
                <div className="h-40 w-full bg-[#F4F4F3] flex items-center justify-center">
                  <span className="text-3xl font-light text-[#0A0A0A]/20 tabular-nums">{year}</span>
                </div>
              )}
              <div className="p-5 flex flex-col gap-2 flex-1">
                <h3 className="text-base font-semibold text-[#0A0A0A] tracking-tight group-hover:underline">
                  {localized(e.title, locale)}
                </h3>
                <div className="flex items-center gap-4 text-xs text-[#737373] mt-auto pt-2">
                  <span>{t("blocks.editionSold", { sold: e.sold_count, total: e.lot_count })}</span>
                  {e.top_price_cents != null && (
                    <span className="font-semibold text-[#1D4D3B]">
                      {t("blocks.editionTop", {
                        amount: new Intl.NumberFormat(locale, { style: "currency", currency: e.currency || "EUR", maximumFractionDigits: 0 }).format(e.top_price_cents / 100),
                      })}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Section>
  );
};

export const EXTENDED_BLOCKS = {
  video: VideoBlock,
  countdown: CountdownBlock,
  story: StoryBlock,
  about: StoryBlock, // Phase 4.5: "About" is a story with its own palette identity
  team: TeamBlock,
  testimonials: TestimonialsBlock,
  cta: CtaBlock,
  social_links: SocialLinksBlock,
  pedigree_highlight: PedigreeHighlightBlock,
  results_highlight: ResultsHighlightBlock,
  how_it_works: HowItWorksBlock,
  legal_terms: LegalTermsBlock,
  register_horse: RegisterHorseBlock,
  vet_info: VetInfoBlock,
  services: ServicesBlock,
  previous_editions: PreviousEditionsBlock,
};
